import { CSSProperties } from 'react'
import dayjs from 'dayjs'
import { colors } from '@lmc/common/constants'
import { Icon, Popover } from '@lmc/ui-kit'
import { isLowerThen564px } from '../../common/utils'

interface CarePlanNextReviewDateProps {
  date?: string
  noFurtherSupportRequired?: boolean
  alert?: {
    type?: string
    text?: string
  }
  style?: CSSProperties
}

export default function CarePlanNextReviewDate({
  date,
  noFurtherSupportRequired,
  alert,
  style,
}: CarePlanNextReviewDateProps) {
  if (!date) return null
  const { result, isOverdue } = getReviewDate(date)
  const isAlert = alert || isOverdue

  let color
  let icon
  if (isAlert) {
    color = colors.red
    icon = 'incident'
  } else {
    const endOfWeek = dayjs().add(7, 'days').endOf('day')
    const isDueThisWeek = dayjs(date).isBefore(endOfWeek)
    color = isDueThisWeek ? colors.orange : colors.green
    icon = isDueThisWeek ? 'clock' : 'tick-in-circle'
  }

  const alertDate =
    alert && alert.type === 'updated' && !isOverdue ? 'Due now' : result

  if (noFurtherSupportRequired) return null
  return (
    <div
      style={{
        ...style,
        ...styles.wrapper,
      }}
    >
      <span
        style={
          {
            ...styles.text,
            textAlign: 'right',
            color: isOverdue ? colors.red : colors.black,
          } as CSSProperties
        }
      >
        {alert ? (
          isLowerThen564px ? (
            <Popover
              placement="bottomRight"
              content={
                <span style={{ ...styles.alert, ...styles.popup }}>
                  {alert.text}
                </span>
              }
            >
              {isLowerThen564px ? alertDate.slice(0, 7) : alertDate}
            </Popover>
          ) : (
            <span>
              {alertDate}
              <br />
              <span style={styles.alert}>{alert.text}</span>
            </span>
          )
        ) : isLowerThen564px ? (
          result.slice(0, 7)
        ) : (
          result
        )}
      </span>
      <Icon
        name={isAlert ? 'ExclamationCircleOutlined' : 'CheckCircleOutlined'}
        style={{
          ...styles.icon,
          ...{ color: isAlert ? colors.red : colors.successgreen },
        }}
      />
    </div>
  )
}

function getReviewDate(date: string) {
  let result = `Due ${dayjs(date).format('DD-MM-YYYY')}`
  let isOverdue = false

  const reviewDate = dayjs(date).startOf('day')
  const today = dayjs().startOf('day')

  if (reviewDate.isSame(today)) {
    result = 'Today'
  }

  if (reviewDate.isAfter(today)) {
    if (dayjs(reviewDate).diff(today, 'days') === 1) {
      result = 'Tomorrow'
    }
  }

  if (reviewDate.isBefore(today)) {
    isOverdue = true
    const overdueDays = dayjs(reviewDate).diff(today, 'days')

    if (overdueDays === -1) {
      result = 'Overdue +1 day'
    } else {
      result = `Overdue +${Math.abs(overdueDays)} days`
    }
  }

  return { result, isOverdue }
}

const styles = {
  icon: {
    alignSelf: 'center',
    marginLeft: 12,
    fontSize: 18,
  },
  text: {
    alignSelf: 'center',
    display: 'block',
    whiteSpace: 'nowrap',
  },
  wrapper: {
    display: 'flex',
  },
  alert: {
    color: colors.darkgrey,
  },
  popup: {
    background: colors.black,
    padding: 5,
    color: colors.white,
    borderRadius: 5,
  },
}
